/**
 * KMS (Knowledge Management System) Frontend Application
 * 
 * This is the main React application component that provides:
 * - Google OAuth2 authentication with session management
 * - Document management and AI-powered search interface
 * - Multi-tenant configuration management
 * - Real-time authentication state handling
 * - Protected routing with automatic redirects
 * 
 * Architecture:
 * - React 19 with TypeScript for type safety
 * - Context-based state management for auth and config
 * - React Router for client-side navigation
 * - Session-based authentication with HTTP-only cookies
 * - Responsive design with Tailwind CSS
 */

import './App.css';
import { Routes, Route, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, Suspense, lazy } from 'react';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import LoginPage from './components/Auth/LoginPage';
import { AuthProvider, useAuth, AuthState } from './contexts/AuthContext';
import { ConfigProvider, useConfig, ConfigState } from './contexts/ConfigContext';

import { Toaster } from 'react-hot-toast';
import AuthCallbackPage from './pages/AuthCallbackPage';

import RecoverAuth from './components/Auth/RecoverAuth';

// Route-based code splitting for better initial load performance
const DocumentManagement = lazy(() => import('./components/document/DocumentManagement'));
const SearchPage = lazy(() => import('./components/search/SearchPage'));
const DocumentsStatusPage = lazy(() => import('./pages/DocumentsStatusPage'));
const UserConfigPage = lazy(() => import('./pages/UserConfigPage'));

// Loading spinner component for Suspense fallback
const PageLoadingSpinner = () => (
  <div className="flex items-center justify-center h-full min-h-[400px]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
      <p className="mt-4 text-gray-500 dark:text-gray-400">Caricamento...</p>
    </div>
  </div>
);

/**
 * Custom hook that manages centralized navigation logic based on authentication and configuration state.
 * 
 * Handles automatic redirects based on:
 * - Authentication status (authenticated/unauthenticated)
 * - Configuration status (configured/unconfigured)
 * - Current page location
 * - Loading states
 * 
 * This ensures users are always on the appropriate page for their current state.
 */
const useNavigationLogic = () => {
  const { authState, isLoading: isAuthLoading } = useAuth();
  const { configState, isConfigLoading, configChecked } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const isOnConfigPage = location.pathname.includes('/configurazioni');
    const isOnLoginPage = location.pathname.includes('/login');
    const isReady = authState !== AuthState.INITIALIZING && configChecked;

    if (isAuthLoading || isConfigLoading || !isReady) {
      // Still loading, do nothing until authentication and configuration checks are complete
      return;
    }
    
    // Unauthenticated users are always redirected to the login page
    if (authState === AuthState.UNAUTHENTICATED) {
      if (!isOnLoginPage) {
        navigate('/login');
      }
      return;
    }

    // Authenticated users have their navigation managed based on config state
    if (authState === AuthState.AUTHENTICATED) {
      if (configState === ConfigState.CONFIGURED) {
        // With configuration, redirect away from login page but allow access to config page
        if (isOnLoginPage) {
          navigate('/documents');
        }
        // Allow users to stay on config page to manage multiple configurations
      } else if (configState === ConfigState.UNCONFIGURED) {
        // Without configuration, allow navigation to documents page for manual uploads
        // The DocumentManagement component handles showing config prompt for Google Drive
        if (isOnLoginPage) {
          navigate('/documents');
        }
      }
      // If configState is CHECKING, INITIALIZING, or a new state, we wait.
    }
  }, [
    authState, 
    configState, 
    configChecked, 
    isAuthLoading, 
    isConfigLoading, 
    location.pathname, 
    navigate
  ]);
};

/**
 * Protected route wrapper component that enforces authentication requirements.
 * 
 * Features:
 * - Uses the centralized navigation hook for automatic redirects
 * - Shows loading spinner during authentication state initialization
 * - Protects all child components from unauthenticated access
 * - Handles both authentication and configuration state transitions
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  useNavigationLogic();
  const { authState, isLoading } = useAuth();

  if (isLoading || authState === AuthState.INITIALIZING) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * Main application layout component for authenticated users.
 * 
 * Provides the core application structure:
 * - Header with user info and logout functionality
 * - Sidebar navigation with active view tracking
 * - Main content area with React Router outlets
 * - Global toast notification system
 * - Responsive layout with proper overflow handling
 */
function MainLayout() {
  const { user, logout } = useAuth();
  const [activeDocMgmtView, setActiveDocMgmtView] = useState<'list' | 'search' | 'documents-status' | 'user-config'>('list');
  const location = useLocation();

  // Update active view based on route
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/documents/status')) {
      setActiveDocMgmtView('documents-status');
    } else if (path.startsWith('/search')) {
      setActiveDocMgmtView('search');
    } else if (path.startsWith('/user-config') || path.startsWith('/configurazioni')) {
      setActiveDocMgmtView('user-config');
    } else {
      setActiveDocMgmtView('list');
    }
  }, [location.pathname]);

  return (
    <div className="flex flex-col h-screen w-screen bg-gray-100 dark:bg-gray-900 overflow-hidden">
      <Header user={user} onLogout={logout} />
      <div className="flex-1 flex overflow-hidden w-full">
        <Sidebar 
          activeView={activeDocMgmtView} 
          user={user}
        />
        <main className="flex-1 w-full overflow-y-auto">
          <Outlet /> 
        </main>
      </div>
      {/* Global toast container */}
      <Toaster position="top-right" />
    </div>
  );
}

/**
 * Root App component that sets up the application structure and routing.
 * 
 * Features:
 * - Context providers for authentication and configuration state
 * - Route definitions for all application pages
 * - Public routes (login, auth callbacks) and protected routes
 * - Authentication error handling and recovery flows
 * - OAuth callback handling for Google authentication
 */
function App() {
  return (
    <AuthProvider>
      <ConfigProvider>
        <div className="app">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth-success" element={<AuthCallbackPage />} />
            <Route path="/auth-failed" element={<AuthFailedPage />} />
            <Route path="/recover-auth" element={<RecoverAuth />} />
            <Route path="/oauth/callback" element={<AuthCallbackPage />} />
            <Route path="/drive-connected" element={<DriveConnectedPage />} />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Suspense fallback={<PageLoadingSpinner />}><DocumentManagement /></Suspense>} />
              <Route path="documents">
                <Route index element={<Suspense fallback={<PageLoadingSpinner />}><DocumentManagement /></Suspense>} />
                <Route path="status" element={<Suspense fallback={<PageLoadingSpinner />}><DocumentsStatusPage /></Suspense>} />
              </Route>
              <Route path="search" element={<Suspense fallback={<PageLoadingSpinner />}><SearchPage /></Suspense>} />
              <Route path="user-config" element={<Suspense fallback={<PageLoadingSpinner />}><UserConfigPage /></Suspense>} />
              <Route path="configurazioni" element={<Suspense fallback={<PageLoadingSpinner />}><UserConfigPage /></Suspense>} />

            </Route>
          </Routes>
        </div>
      </ConfigProvider>
    </AuthProvider>
  );
}

/**
 * Authentication failure page component.
 * 
 * Displays user-friendly error messages for various OAuth failure scenarios:
 * - State mismatch errors (security/session issues)
 * - User info retrieval failures
 * - Session storage problems
 * - Credential persistence issues
 * - General callback exceptions
 * 
 * Provides a "Try Again" button to return to the login page.
 */
/**
 * Drive connected success page.
 *
 * Displayed after a user successfully connects their Google Drive.
 * Auto-refreshes auth state and redirects to documents page.
 */
const DriveConnectedPage: React.FC = () => {
  const navigate = useNavigate();
  const { checkAuthStatus } = useAuth();

  useEffect(() => {
    // Refresh auth state to pick up the new Drive connection
    const refreshAndRedirect = async () => {
      await checkAuthStatus(true); // Force refresh
      // Redirect to documents page after a brief delay
      setTimeout(() => {
        navigate('/documents');
      }, 1500);
    };
    refreshAndRedirect();
  }, [checkAuthStatus, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-md">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Google Drive Connected!
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Your Google Drive has been successfully connected.
          You can now access and search your documents.
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Redirecting to documents...
        </p>
        <div className="mt-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    </div>
  );
};

const AuthFailedPage: React.FC = () => {
  const navigate = useNavigate();
  
  const urlParams = new URLSearchParams(window.location.search);
  const error = urlParams.get('error');
  const details = urlParams.get('details');
  
  let errorMessage = 'An unknown error occurred during authentication.';
  
  switch (error) {
    case 'state_mismatch':
      errorMessage = 'OAuth state verification failed. This may be due to session timeout or security issues.';
      break;
    case 'failed_user_info':
      errorMessage = 'Failed to retrieve user information from Google. Please check your Google account permissions.';
      break;
    case 'session_save_failed':
      errorMessage = 'Failed to save authentication data to session. This may be a server configuration issue.';
      break;
    case 'credentials_not_persisted':
      errorMessage = 'Authentication succeeded but credentials were not properly stored. This may be a session storage issue.';
      break;
    case 'callback_exception':
      errorMessage = `Authentication callback encountered an exception${details ? ': ' + details.replace(/_/g, ' ') : ''}.`;
      break;
    default:
      if (error) {
        errorMessage = `Authentication failed with error: ${error}`;
      }
  }
  
  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>Authentication Failed</h2>
      <div style={{ marginTop: '20px' }}>
        <div>
          <p style={{ color: '#d32f2f', marginBottom: '10px' }}>{errorMessage}</p>
          {error && (
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
              Error Code: {error}
            </p>
          )}
          {details && (
            <p style={{ fontSize: '12px', color: '#999', marginBottom: '20px' }}>
              Details: {details.replace(/_/g, ' ')}
            </p>
          )}
        </div>
      </div>
      <button 
        onClick={() => navigate('/login')} 
        style={{
          padding: '10px 20px',
          backgroundColor: '#1976d2',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Try Again
      </button>
    </div>
  );
};

export default App;
